import { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import Swal from 'sweetalert2';
import experienceService from '../../../services/api/experienceService';
import { Tab, Button } from 'semantic-ui-react';
import { clickableStyles } from '../../../components/helpers';
import { cancelThisWeekendService, thisWeekendService } from './confirmationHelpers';
import { VolunteersServiceAreaModal } from '../../volunteer/components/volunteersCountServiceAreas';
import AGTable from '../../../components/table/aggrid';
import { ExperienceTeamAreaRowDef } from '../../../components/common/rows/ExperienceTeamAreaRow';
import {
  isCaptchaRequired,
  useRecaptchaToken,
  RECAPTCHA_SECURITY_ERROR,
} from 'src/components/helpers/recaptchaUtils';

const ConfirmedVolunteers = (props) => {
  const {
    set_loading,
    loading,
    eventSlugname,
    serviceArea,
    section,
    role,
    handleDate,
    showAreaCount,
  } = props;
  const [volunteersByEvent, set_volunteersByEvent] = useState([]);
  const [volunteersRowChecks, set_volunteersRowChecks] = useState([]);
  const [showImgs, set_showImgs] = useState(true);
  const getCaptchaToken = useRecaptchaToken('volunteer_service_logs');

  useEffect(() => {
    getVolunteersByEvent();
  }, [eventSlugname, serviceArea, section, role, handleDate]);

  const getVolunteersByEvent = async () => {
    set_loading?.(true);

    const captchaRequired = isCaptchaRequired();
    const captcha = await getCaptchaToken({ skip: !captchaRequired });
    if (captchaRequired && !captcha) {
      set_loading?.(false);
      Swal.fire(RECAPTCHA_SECURITY_ERROR);
      return;
    }

    experienceService
      .getAllVolunteerServiceLogs({
        eventSlugname,
        serviceArea,
        section,
        role,
        startDate: handleDate?.startDate?.toDate(),
        endDate: handleDate?.endDate?.toDate(),
        ...(captchaRequired ? { captcha } : {}),
      })
      .then((response) => {
        set_loading(false);
        if (response.data.success) {
          set_volunteersByEvent(response.data.volunteersByEvent);
        } else if (response.data.message) {
          Swal.fire(response.data.message);
        }
      })
      .catch((err) => {
        set_loading?.(false);
        if (err?.response?.data?.message) Swal.fire(err.response.data.message);
      });
  };

  const uniqueVolunteers = useCallback((eventVolunteers) =>
    eventVolunteers?.reduce((acc, volunteer) => {
      if (!acc.some((vol) => vol?.Volunteer?.guest_id === volunteer?.Volunteer?.guest_id)) {
        acc.push(volunteer);
      }
      return acc;
    }, [])
  );

  // Column definitions for AG Grid
  const getColumnDefs = (service, eventVolunteers) => {
    const volunteers = uniqueVolunteers(eventVolunteers)?.map((v) => v?.Volunteer);

    const isVolunteerReserved = (volunteerId) => {
      return eventVolunteers?.some(
        (reserved) => Number(reserved?.Volunteer?.id) === Number(volunteerId)
      );
    };

    const isVolunteerChecked = (volunteerId) => {
      return volunteersRowChecks?.some(
        (volunteerCheck) => volunteerCheck.volunteer_id === volunteerId
      );
    };

    const handleCheckboxChange = (volunteer) => {
      const { id, Guest, suspended } = volunteer;

      if (suspended) return;

      if (isVolunteerChecked(id)) {
        set_volunteersRowChecks((prev) =>
          prev.filter((volunteerCheck) => volunteerCheck.volunteer_id !== id)
        );
      } else {
        set_volunteersRowChecks((prev) => [...prev, { guest_id: Guest.id, volunteer_id: id }]);
      }
    };

    const handleSelectAll = () => {
      const nonSuspendedVolunteers = volunteers?.filter((v) => !v?.suspended);

      if (volunteersRowChecks?.length === nonSuspendedVolunteers?.length) {
        set_volunteersRowChecks([]);
      } else {
        set_volunteersRowChecks(
          nonSuspendedVolunteers?.map((volunteer) => ({
            guest_id: volunteer?.Guest?.id,
            volunteer_id: volunteer?.id,
          }))
        );
      }
    };

    return ExperienceTeamAreaRowDef({
      loading,
      service,
      eventSlugname,
      getVolunteersByEvent,
      set_loading,
      showImgs,
      isVolunteerReserved,
      isVolunteerChecked,
      handleCheckboxChange,
      handleSelectAll,
      volunteersRowChecks,
      volunteers,
      showArea: true,
      showLeaders: false,
      showEdit: false,
      showActive: false,
      grouped: true,
    });
  };

  return (
    <div style={{ margin: '20px 0 0 0' }}>
      <Tab
        panes={volunteersByEvent.map((service) => ({
          menuItem: moment(service?.date).format('dddd, DD [de] MMM'),
          render: () => (
            <Tab.Pane attached={false} key={service.id}>
              {volunteersRowChecks.length > 0 && (
                <>
                  <p style={{ margin: '20px 0 0 0' }}>
                    Seleccionados: {volunteersRowChecks.length} -{' '}
                    <span style={clickableStyles} onClick={() => set_volunteersRowChecks([])}>
                      Limpiar
                    </span>
                  </p>
                  <Button
                    primary
                    disabled={loading}
                    onClick={() =>
                      thisWeekendService(
                        volunteersRowChecks,
                        service,
                        eventSlugname,
                        {},
                        getVolunteersByEvent,
                        set_loading
                      )
                    }
                  >
                    Confirmar seleccionados
                  </Button>
                  <Button
                    disabled={loading}
                    onClick={() =>
                      cancelThisWeekendService(
                        volunteersRowChecks.map((volunteer) => volunteer.volunteer_id),
                        service,
                        eventSlugname,
                        {},
                        getVolunteersByEvent,
                        set_loading
                      )
                    }
                  >
                    Cancelar seleccionados
                  </Button>
                </>
              )}
              <p>Total en pantalla: {uniqueVolunteers(service?.EventVolunteers)?.length}</p>
              <div style={{ padding: '20px 0 0 0' }}>
                <Button primary onClick={() => set_showImgs(!showImgs)}>
                  {showImgs ? 'Ocultar' : 'Mostrar'} fotos
                </Button>
                {!!showAreaCount && (
                  <VolunteersServiceAreaModal
                    volunteers={uniqueVolunteers(service?.EventVolunteers)}
                  />
                )}
              </div>

              <AGTable
                rowData={uniqueVolunteers(service?.EventVolunteers)?.map((v) => v?.Volunteer)}
                columnDefs={getColumnDefs(service, service?.EventVolunteers)}
                rowHeight={showImgs ? 90 : 60}
                defaultCol={{
                  suppressMovable: true,
                }}
              />
            </Tab.Pane>
          ),
        }))}
      />
    </div>
  );
};

export default ConfirmedVolunteers;
